import { useField, useFormikContext } from 'formik';
import { ReactNode } from 'react';

import { ContractPriceListStatus, PriceListActivationMode } from '../../../../types/ContractMessage';
import { Button } from '../../../cdl/Button/Button';
import { Checkbox } from '../../../cdl/Checkbox/Checkbox';
import { IconButton } from '../../../cdl/IconButton/IconButton';
import { Text } from '../../../cdl/Text/Text';
import { FormikDatePicker } from '../../../common/form-elements/formik/FormikDatePicker';
import { translate } from '../../../common/helpers/translate.helper';
import { IconArrowRight } from '../../../common/icons/cdl/ArrowRight';
import { IconTrash } from '../../../common/icons/cdl/Trash';
import { Box } from '../../../common/ui/Box';

import { PriceListFormValue } from './types';

interface PriceListEditFormProps {
    activationMode: PriceListActivationMode;
    index: number;
    fileName: ReactNode;
    disableActiveOnApproval?: boolean;
    activeOnApprovalDescription?: string;
    onSave?: () => void;
    onCancel?: () => void;
    onDeleteFile?: () => void;
    isEdit?: boolean;
    isSubmitting?: boolean;
}

export const PriceListEditForm = ({
    activationMode,
    index,
    fileName,
    disableActiveOnApproval = false,
    activeOnApprovalDescription,
    onSave,
    onCancel,
    onDeleteFile,
    isEdit = false,
    isSubmitting = false,
}: PriceListEditFormProps) => {
    const [activeOnApprovalField, , activeOnApprovalHelpers] = useField<boolean>(
        `priceLists[${index}].activeOnApproval`
    );
    const [statusField] = useField<ContractPriceListStatus | undefined>(`priceLists[${index}].status`);
    const [initialStatusField] = useField<ContractPriceListStatus | undefined>(`priceLists[${index}].initialStatus`);
    const { setFieldValue } = useFormikContext<{ priceLists: PriceListFormValue[] }>();
    const initialStatus = initialStatusField.value ?? statusField.value;
    const isProcessingPriceList = initialStatus === ContractPriceListStatus.PROCESSING;

    const handleActiveOnApprovalChange = (checked: boolean) => {
        if (!isProcessingPriceList) {
            setFieldValue(
                `priceLists[${index}].status`,
                checked ? ContractPriceListStatus.ACTIVE : initialStatus,
                false
            );
            return;
        }

        if (!checked) {
            activeOnApprovalHelpers.setValue(false);
            return;
        }

        activeOnApprovalHelpers.setValue(true);
    };

    return (
        <Box display="flex" flexDirection="column" gap={16}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box display="flex" flexDirection="column" gap={4}>
                    <Text variant="fieldLabel">{translate('contracts.priceLists.priceList')}</Text>
                    <Text variant="small" color="accent.foreground">
                        {fileName}
                    </Text>
                </Box>
                {!isEdit && onDeleteFile ? (
                    <IconButton
                        Icon={IconTrash}
                        emphasis="transparent"
                        size="medium"
                        type="button"
                        onClick={onDeleteFile}
                        tooltipLabel={translate('contracts.priceLists.deleteFile')}
                    />
                ) : null}
            </Box>

            <Box display="flex" flexDirection="column" gap={8}>
                <Text variant="fieldLabel">{translate('contracts.priceLists.duration')}</Text>
                <Box display="flex" alignItems="flex-start" gap={8}>
                    <Box flex={1}>
                        <FormikDatePicker
                            name={`priceLists[${index}].validFrom`}
                            label={translate('contracts.priceLists.startDate')}
                            placeholder={translate('contracts.priceLists.startDatePlaceholder')}
                        />
                    </Box>
                    <Box display="flex" alignItems="center" paddingTop={36}>
                        <IconArrowRight width={16} height={16} />
                    </Box>
                    <Box flex={1}>
                        <FormikDatePicker
                            name={`priceLists[${index}].validUntil`}
                            label={translate('contracts.priceLists.endDate')}
                            placeholder={translate('contracts.priceLists.endOfContract')}
                            isClearable
                        />
                    </Box>
                </Box>
            </Box>

            {!isEdit && activationMode === PriceListActivationMode.MANUAL ? (
                <Checkbox
                    checked={activeOnApprovalField.value}
                    label={translate('contracts.priceLists.activatePriceList')}
                    description={
                        activeOnApprovalDescription ?? translate('contracts.priceLists.activatePriceListDescription')
                    }
                    disabled={disableActiveOnApproval}
                    onChange={(event) => handleActiveOnApprovalChange(event.target.checked)}
                />
            ) : null}

            {!isEdit ? (
                <Box display="flex" justifyContent="flex-end" gap={8}>
                    <Button type="button" onClick={onCancel} disabled={isSubmitting}>
                        {translate('contracts.priceLists.cancel')}
                    </Button>
                    <Button type="button" emphasis="high" onClick={onSave} isLoading={isSubmitting}>
                        {translate('contracts.priceLists.save')}
                    </Button>
                </Box>
            ) : null}
        </Box>
    );
};
